import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { AdmissionCycle, AdmissionField } from '../../../types/admissions';
import {
  Page, Section, NumberedList, Signatures,
  pdfColors, pdfFontSize, pdfLetter, pdfRadius, pdfSpacing,
  type SchoolBranding, type DocumentMeta,
} from '../../../utils/pdf';
import { FieldBlock, FormSections, printableSections } from './sharedFieldRendering';
import { documentsAcrossPathways } from '../../../utils/admissionAssessment';

interface Props {
  cycle: AdmissionCycle;
  branding: SchoolBranding;
  meta: DocumentMeta;
}

const FOOTER_NOTE = 'Please write in BLOCK LETTERS. Fields marked * are mandatory.';

const DECLARATION =
  'I declare that the information given in this form is true and complete to the best of my ' +
  'knowledge. I understand that admission may be cancelled if any information is found to be ' +
  'incorrect, and I agree to abide by the rules and fee policy of the school.';

const styles = StyleSheet.create({
  // ── Office-use row ───────────────────────────────────────────────────
  row: {
    flexDirection: 'row',
    marginHorizontal: -pdfSpacing.md,
  },
  col: {
    flex: 1,
    paddingHorizontal: pdfSpacing.md,
  },

  // ── Documents checklist ──────────────────────────────────────────────
  checkGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  checkItem: {
    width: '50%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 4,
    paddingRight: pdfSpacing.md,
  },
  checkBox: {
    width: 10,
    height: 10,
    borderWidth: 0.8,
    borderColor: pdfColors.textSecondary,
    borderStyle: 'solid',
    borderRadius: 1.5,
    marginRight: 7,
    marginTop: 1,
  },
  checkBody: { flex: 1 },
  checkText: {
    fontSize: pdfFontSize.sm,
    color: pdfColors.textPrimary,
  },
  checkTag: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 600,
    color: pdfColors.textMuted,
    lineHeight: 1.4,
    marginTop: 2,
  },

  // ── Declaration + office use ─────────────────────────────────────────
  declaration: {
    fontSize: pdfFontSize.sm,
    color: pdfColors.textSecondary,
    lineHeight: 1.5,
    marginBottom: pdfSpacing.sm,
  },
  office: {
    borderWidth: 0.8,
    borderStyle: 'dashed',
    borderColor: pdfColors.dividerStrong,
    borderRadius: pdfRadius.md,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 4,
  },
  officeTitle: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openStrong,
    color: pdfColors.textMuted,
    marginBottom: 2,
  },
});

const OFFICE_FIELDS: AdmissionField[] = [
  { key: 'formNo', label: 'Form No.' },
  { key: 'receivedOn', label: 'Received on', type: 'date' },
  { key: 'receivedBy', label: 'Received by' },
  { key: 'feeReceipt', label: 'Fee receipt No.' },
].map((f, i) => ({
  type: 'text', required: false, enabled: true, isBuiltIn: false, isSystem: false, order: i, ...f,
} as AdmissionField));

/** BlankFormDocument — printable admission form for walk-in parents: every
 *  field of the cycle's form with a writing space matched to its type, then
 *  the document checklist, a declaration and an office-use block. */
const BlankFormDocument: React.FC<Props> = ({ cycle, branding, meta }) => {
  let sectionNo = printableSections(cycle).length;
  const documents = documentsAcrossPathways(cycle);
  return (
    <Document title={meta.title} author={branding.name}>
      <Page branding={branding} meta={{ ...meta, footerNote: meta.footerNote ?? FOOTER_NOTE }}>
        <FormSections cycle={cycle} />

        {/* ── Documents checklist ─────────────────────── */}
        {documents.length > 0 && (
          <Section
            index={++sectionNo}
            title="Documents to attach"
            caption="Attach photocopies; bring originals for verification"
          >
            <View style={styles.checkGrid}>
              {documents.map((d) => (
                <View key={d.item.key} style={styles.checkItem} wrap={false}>
                  <View style={styles.checkBox} />
                  <View style={styles.checkBody}>
                    <Text style={styles.checkText}>{d.item.label}</Text>
                    <Text style={styles.checkTag}>{d.tag}</Text>
                  </View>
                </View>
              ))}
            </View>
          </Section>
        )}

        {/* ── Instructions ────────────────────────────── */}
        {cycle.admitCardInstructions?.length > 0 && (
          <Section index={++sectionNo} title="Important instructions">
            <NumberedList items={cycle.admitCardInstructions} />
          </Section>
        )}

        {/* ── Declaration ─────────────────────────────── */}
        <Section index={++sectionNo} title="Declaration by parent / guardian" keepTogether>
          <Text style={styles.declaration}>{DECLARATION}</Text>
          <Signatures slots={['Signature of parent / guardian', 'Name', 'Date']} />
        </Section>

        {/* ── Office use ──────────────────────────────── */}
        <View style={styles.office} wrap={false}>
          <Text style={styles.officeTitle}>For office use only</Text>
          <View style={styles.row}>
            {OFFICE_FIELDS.map((f) => (
              <View key={f.key} style={styles.col}>
                <FieldBlock field={f} />
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default BlankFormDocument;
